import React, { useCallback, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import SessionPlayerPill from './SessionPlayerPill';
import { formatNameForDisplay } from '../../utils/nameUtils';
import { getPlayerNameColor, getDisplayElo, getSinglesClubEloRating } from '../../utils/playerUtils';
import AnimatedNumber from '../../utils/AnimatedNumber';
import CourtPanel from './CourtPanel';
import TeamPanel from './TeamPanel';
import Button from '../common/Button';
import PartnershipSplitNotice from './PartnershipSplitNotice';
import MatchmakingRelaxedBadge from './MatchmakingRelaxedBadge';
import ScheduledGameLockBadge from './ScheduledGameLockBadge';
import PegBoardPickerHint, {
  PegBoardFillFirstHint,
  PegBoardWaitingForGame,
  PegBoardYourTurnHint,
  pegBoardPickerFromQueue,
} from '../pegBoard/PegBoardPickerHint';
import {
  formatPegBoardPickTargetLabel,
  isPegBoardPickTargetNextUp,
} from '../../utils/pegBoard';
import { isAssignmentRowLockedByScheduledGame } from '../../utils/scheduledGameConsumption';
import { isSessionMensDoublesEloExemptLineup } from '@badminton/matchmaking-core';

/** When true, shows "?" with matchmaking debug on upcoming courts (local dev / variety flag). */
const SHOW_UPCOMING_MATCHMAKING_DEBUG_TOOLTIP =
  process.env.REACT_APP_VARIETY_AWARE_POOL_SELECTION === 'true'
  || process.env.REACT_APP_SHOW_UPCOMING_MATCHMAKING_DEBUG === 'true';

// Debug tooltip component that shows top 3 matchmaking combinations
function MatchmakingDebugTooltip({ debugInfo, playersById }) {
  const [isHovered, setIsHovered] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const triggerRef = useRef(null);

  useEffect(() => {
    if (isHovered && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const tooltipWidth = 400;
      const tooltipHeight = 300;
      
      let top = rect.bottom + 8; // Position below
      let left = rect.right - tooltipWidth; // Align to right edge
      
      // Ensure tooltip doesn't go off screen
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      if (left < 8) {
        left = 8;
      }
      if (left + tooltipWidth > viewportWidth) {
        left = viewportWidth - tooltipWidth - 8;
      }
      
      if (top + tooltipHeight > viewportHeight) {
        top = rect.top - tooltipHeight - 8; // Position above if not enough space below
      }
      if (top < 8) {
        top = 8;
      }
      
      setTooltipStyle({
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        zIndex: 99999
      });
    }
  }, [isHovered]);

  if (!debugInfo || (
    (!debugInfo.topCombinations || debugInfo.topCombinations.length === 0)
    && !debugInfo.varietyMetrics
  )) {
    return null;
  }

  const varietyMetrics = debugInfo.varietyMetrics;
  const hasCombinations = debugInfo.topCombinations && debugInfo.topCombinations.length > 0;

  return (
    <>
      <span 
        ref={triggerRef}
        className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-info-light text-info text-xs font-semibold cursor-help hover:bg-info/20 transition-colors"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        title="Matchmaking debug info"
      >
        ?
      </span>
      {isHovered && createPortal(
        <div 
          className="px-4 py-3 bg-surface border-2 border-border-medium rounded-lg shadow-xl max-w-md pointer-events-none"
          style={tooltipStyle}
        >
          <div className="font-bold mb-2 text-sm text-text-primary border-b border-border-medium pb-1">
            {hasCombinations ? 'Top 3 Matchmaking Combinations' : 'Matchmaking Variety Metrics'}
          </div>
          {varietyMetrics && (
            <div className="mb-3 p-2 rounded border border-border-medium bg-surface-subtle text-xs space-y-1">
              <div className="font-semibold text-text-primary">Session saturation</div>
              <div className="text-text-secondary">
                Score: <span className="font-medium">{Number(varietyMetrics.sessionSaturationScore ?? 0).toFixed(3)}</span>
                {' · '}
                Pair sat: {Number(varietyMetrics.pairSaturation ?? 0).toFixed(3)}
                {' · '}
                Repeat quartets: {Number(varietyMetrics.repeatQuartetRate ?? 0).toFixed(3)}
              </div>
              {varietyMetrics.shapingReason && (
                <div className="text-text-muted">Shaping: {varietyMetrics.shapingReason}</div>
              )}
              {varietyMetrics.shapingApplied && (
                <div className="text-info">Variety shaping applied</div>
              )}
            </div>
          )}
          {hasCombinations && (
          <>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {debugInfo.topCombinations.map((combo, idx) => {
              const isSelected = idx === 0;
              return (
                <div 
                  key={idx}
                  className={`p-2 rounded border ${isSelected ? 'bg-success-light border-success-dark' : 'bg-surface-subtle border-border-medium'}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs font-semibold ${isSelected ? 'text-success-dark' : 'text-text-secondary'}`}>
                      {isSelected ? '✓ Selected' : `#${idx + 1}`}
                    </span>
                    <span className={`text-xs font-bold ${isSelected ? 'text-success-dark' : 'text-text-secondary'}`}>
                      Score: {combo.score.toFixed(0)}
                    </span>
                  </div>
                  <div className="text-xs space-y-0.5">
                    <div className="flex items-center gap-1">
                      <span className="font-medium text-team1">Team 1:</span>
                      <span className="text-text-primary">{combo.team1Names.join(' + ')}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="font-medium text-team2">Team 2:</span>
                      <span className="text-text-primary">{combo.team2Names.join(' + ')}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {debugInfo.selectedScore !== null && (
            <div className="mt-2 pt-2 border-t border-blue-200 text-xs text-gray-600">
              Selected score: <span className="font-semibold text-green-700">{debugInfo.selectedScore.toFixed(0)}</span>
            </div>
          )}
          {debugInfo.relaxationLevel !== undefined && debugInfo.relaxationLevel > 0 && (
            <div className="mt-2 pt-2 border-t border-orange-200 bg-orange-50 rounded p-2">
              <div className="text-xs font-semibold text-orange-800 mb-1">
                ⚡ Relaxation Applied
              </div>
              <div className="text-xs text-orange-700">
                <span className="font-medium">Level {debugInfo.relaxationLevel}:</span>{' '}
                {debugInfo.relaxationName || 'Constraints relaxed'}
              </div>
            </div>
          )}
          </>
          )}
        </div>,
        document.body
      )}
    </>
  );
}

function UpcomingPlayerPill({ player, team, onRemove, currentClubId, teammate, showEloTooltip = true }) {
  return (
    <SessionPlayerPill
      player={player}
      teammate={teammate}
      variant="upcoming"
      onRemove={onRemove}
      team={team}
      removeTitle="Remove from upcoming match"
      showEloTooltip={showEloTooltip}
    />
  );
}

const UpcomingMatch = ({
  match,
  onRemovePlayer,
  onAddPlayerToUpcoming,
  /** Host: clear all players from this queued match and return them to the waitlist */
  onClearUpcomingMatch,
  onFillUpcomingCourt,
  currentClubId = 'default',
  matchIndex = 0,
  playersById,
  /** Active waitlist + players on other upcoming matches (pick pool, section 1) */
  availablePlayers = [],
  /** Signed up / paused — not on court or upcoming; adds as playing */
  waitingManagementPlayers = [],
  sessionPlayerStatus = {},
  nextAssignments = [],
  matchmakingValidation = { possible: true, reason: '' },
  playersPerSide = 2,
  /** Align with active courts (session uses compact grid) */
  gridDensity = 'compact',
  playerWaitMetrics = {},
  /** 'full' | 'mobilePlayer' — mobilePlayer renders compact side-by-side on mobile for player viewers */
  courtCardLayout = 'full',
  /** Viewer's player ID for "(You)" highlight in mobile compact layout */
  viewerPlayerId = null,
  partnerships = [],
  scheduledGames = [],
  showEloTooltip = true,
  pegBoardQueue = null,
  pegBoardPickTarget = null,
  onOpenPegBoardPick,
}) => {
  // Calculate team average ELO for the current club
  const getTeamAvgElo = (teamIds) => {
    const valid = teamIds.filter((id) => playersById[id]);
    if (valid.length === 0) return 0;
    if (playersPerSide === 1 && valid.length === 1) {
      return getSinglesClubEloRating(playersById[valid[0]]);
    }
    if (valid.length === 2) {
      const [p1Id, p2Id] = valid;
      const p1 = playersById[p1Id];
      const p2 = playersById[p2Id];
      const p1Elo = p1 && p2 && p1.gender && p2.gender && p1.gender !== p2.gender
        ? (p1.mixed_elo_rating || 0)
        : (p1?.levels_elo_rating || 0);
      const p2Elo = p1 && p2 && p1.gender && p2.gender && p1.gender !== p2.gender
        ? (p2.mixed_elo_rating || 0)
        : (p2?.levels_elo_rating || 0);
      return Math.round((p1Elo + p2Elo) / 2);
    }
    if (valid.length === 1) {
      const p = playersById[valid[0]];
      return Math.round(Number(p?.levels_elo_rating) || 0);
    }
    return Math.round(
      valid.reduce((sum, id) => {
        const player = playersById[id];
        return sum + (player?.levels_elo_rating || 0);
      }, 0) / (valid.length || 1)
    );
  };
  
  const team1AvgElo = getTeamAvgElo(match.team1);
  const team2AvgElo = getTeamAvgElo(match.team2);
  
  /** Roster is tied to a scheduled game — only cancelling it (Manage → Scheduled games) can change the lineup. */
  const rosterLocked = isAssignmentRowLockedByScheduledGame(match, scheduledGames);

  // Wrapper to adapt TeamPanel's onRemovePlayer signature to the expected one
  const handleRemovePlayer = (playerId, teamNumber, courtNumber) => {
    if (onRemovePlayer) {
      onRemovePlayer(playerId, teamNumber, matchIndex);
    }
  };
  // Wrapper for add player
  const handleAddPlayer = (teamNumber, courtNumber) => {
    if (onAddPlayerToUpcoming) {
      onAddPlayerToUpcoming(teamNumber, matchIndex);
    }
  };

  // Handler for when a player is actually selected from the modal
  const handlePlayerSelected = (playerId, courtNumber, teamNumber) => {
    if (onAddPlayerToUpcoming) {
      onAddPlayerToUpcoming(playerId, teamNumber, matchIndex);
    }
  };

  // Check if upcoming match has empty spots or is full
  const hasEmptySpots =
    match.team1.length < playersPerSide || match.team2.length < playersPerSide;
  const isFull =
    match.team1.length === playersPerSide && match.team2.length === playersPerSide;
  const isCompletelyEmpty = match.team1.length === 0 && match.team2.length === 0;
  const isPegBoardPickTargetSlot = isPegBoardPickTargetNextUp(pegBoardPickTarget, matchIndex);
  const pegBoardPicker =
    isCompletelyEmpty && isPegBoardPickTargetSlot
      ? pegBoardPickerFromQueue(pegBoardQueue, playersById)
      : null;
  const pegBoardFillFirstLabel =
    isCompletelyEmpty && pegBoardPickTarget && !isPegBoardPickTargetSlot
      ? formatPegBoardPickTargetLabel(pegBoardPickTarget)
      : '';
  const isPegBoardEmptyCourt = Boolean(pegBoardQueue) && isCompletelyEmpty;
  const pegBoardPickIsViewerTurn =
    Boolean(viewerPlayerId && pegBoardPicker?.id && viewerPlayerId === pegBoardPicker.id);
  const showPegBoardPick =
    Boolean(onOpenPegBoardPick) && isCompletelyEmpty && isPegBoardPickTargetSlot;
  const pegBoardPickButton = showPegBoardPick ? (
    <Button
      type="button"
      onClick={onOpenPegBoardPick}
      variant="primary"
      size="small"
      className="w-full min-h-[44px] sm:min-h-0"
      title="Pick the next game from the queue"
    >
      {pegBoardPickIsViewerTurn ? 'Your turn to pick the game' : 'Pick game'}
    </Button>
  ) : null;
  const hasAnyPlayers = match.team1.length > 0 || match.team2.length > 0;
  const canMatchmake = matchmakingValidation.possible;
  /** Later slots must wait until every earlier upcoming match is a full game (correct matchmaking order). */
  const priorSlotsReady =
    matchIndex === 0 ||
    (Array.isArray(nextAssignments) &&
      nextAssignments.slice(0, matchIndex).every(
        (m) =>
          (m?.team1?.length ?? 0) === playersPerSide &&
          (m?.team2?.length ?? 0) === playersPerSide
      ));
  const createGameBlockedByOrder = matchIndex > 0 && !priorSlotsReady;
  const firstIncompletePriorIndex =
    matchIndex > 0 && Array.isArray(nextAssignments)
      ? nextAssignments.slice(0, matchIndex).findIndex(
          (m) =>
            (m?.team1?.length ?? 0) !== playersPerSide ||
            (m?.team2?.length ?? 0) !== playersPerSide
        )
      : -1;
  const orderBlockCourtLabel =
    firstIncompletePriorIndex >= 0 ? firstIncompletePriorIndex + 1 : matchIndex;
  const shouldDisableFillCourt = !isCompletelyEmpty || !canMatchmake || createGameBlockedByOrder;
  
  // Handle fill upcoming court
  const handleFillUpcomingCourt = async () => {
    if (onFillUpcomingCourt) {
      try {
        await onFillUpcomingCourt(matchIndex);
      } catch (error) {
        console.error('UpcomingMatch: Error calling onFillUpcomingCourt:', error);
        alert('Error filling court. Please try again.');
      }
    }
  };

  

  // Render a team panel - memoized to prevent excessive re-renders
  const renderTeamPanel = useCallback((playerIds, teamNumber, playersById) => {
    const avgElo = getTeamAvgElo(playerIds);
    return (
      <TeamPanel
        team={playerIds}
        teamNumber={teamNumber}
        playersById={playersById}
        avgElo={avgElo}
        canRemove={!!onRemovePlayer}
        onRemovePlayer={handleRemovePlayer}
        onAddPlayer={handlePlayerSelected}
        courtNumber={matchIndex}
        availablePlayers={availablePlayers}
        waitingManagementPlayers={waitingManagementPlayers}
        sessionPlayerStatus={sessionPlayerStatus}
        nextAssignments={nextAssignments}
        onRemoveFromUpcoming={
          onRemovePlayer
            ? (playerId, teamNumber, sourceMatchIndex) =>
                onRemovePlayer(playerId, teamNumber, sourceMatchIndex)
            : undefined
        }
        emptySlotsReadOnly={!onAddPlayerToUpcoming || rosterLocked}
        maxPlayersPerTeam={playersPerSide}
        courtPlayerDragEnabled={!rosterLocked && !!onRemovePlayer}
        slotDndPrefix={`upcoming-${matchIndex}`}
        density={gridDensity}
        playerWaitMetrics={playerWaitMetrics}
        showEloTooltip={showEloTooltip}
        pegBoardQueue={pegBoardQueue}
      />
    );
  }, [
    rosterLocked,
    handleRemovePlayer,
    handlePlayerSelected,
    matchIndex,
    availablePlayers,
    waitingManagementPlayers,
    sessionPlayerStatus,
    nextAssignments,
    onRemovePlayer,
    onAddPlayerToUpcoming,
    playersPerSide,
    gridDensity,
    playerWaitMetrics,
    showEloTooltip,
    pegBoardQueue,
  ]);

  const isSessionMensDoubles = isSessionMensDoublesEloExemptLineup(
    match.team1 || [],
    match.team2 || [],
    playerWaitMetrics,
    playersById
  );

  return (
    <CourtPanel
      title={
        <div className="flex items-center gap-2 flex-wrap">
          <span>{`Upcoming Match #${matchIndex + 1}`}</span>
          <MatchmakingRelaxedBadge
            relaxed={match.matchmakingRelaxed}
            relaxationDetails={match.matchmakingRelaxationDetails}
            variant="upcoming"
          />
          {rosterLocked && <ScheduledGameLockBadge />}
          {isSessionMensDoubles && (
            <span
              className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
              title="Session mens doubles — club ELO ratings not updated"
              aria-label="Session mens doubles, no club ELO update"
            >
              ♂ Session mens · no ELO
            </span>
          )}
        </div>
      }
      headerRight={
        (SHOW_UPCOMING_MATCHMAKING_DEBUG_TOOLTIP && match.matchmakingDebugInfo) ||
        (hasAnyPlayers && onClearUpcomingMatch) ? (
          <div className="flex items-center gap-2">
            {SHOW_UPCOMING_MATCHMAKING_DEBUG_TOOLTIP && match.matchmakingDebugInfo ? (
              <MatchmakingDebugTooltip debugInfo={match.matchmakingDebugInfo} playersById={playersById} />
            ) : null}
            {onClearUpcomingMatch && hasAnyPlayers ? (
              <button
                type="button"
                onClick={() => onClearUpcomingMatch(matchIndex)}
                className="text-slate-500 hover:text-red-600 focus:outline-none"
                title="Clear upcoming match — return all players to the queue"
                aria-label="Clear upcoming match and return players to the queue"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            ) : null}
          </div>
        ) : SHOW_UPCOMING_MATCHMAKING_DEBUG_TOOLTIP && match.matchmakingDebugInfo ? (
          <MatchmakingDebugTooltip debugInfo={match.matchmakingDebugInfo} playersById={playersById} />
        ) : null
      }
      teams={[match.team1, match.team2]}
      playersById={playersById}
      renderTeamPanel={(team, idx, playersById) => renderTeamPanel(team, idx, playersById)}
      bodyFooter={
        hasAnyPlayers ? (
          <PartnershipSplitNotice
            team1={match.team1}
            team2={match.team2}
            partnerships={partnerships}
            playersById={playersById}
          />
        ) : null
      }
      actions={
        showPegBoardPick && !isPegBoardEmptyCourt ? (
          pegBoardPickButton
        ) : onFillUpcomingCourt ? (
          <div className="flex flex-wrap justify-between items-center gap-2">
            <div className="flex flex-col items-start gap-2 max-w-full">
              <Button
                onClick={handleFillUpcomingCourt}
                variant={
                  isCompletelyEmpty && canMatchmake && !createGameBlockedByOrder ? "primary" : "secondary"
                }
                disabled={shouldDisableFillCourt}
                title={
                  !canMatchmake
                    ? matchmakingValidation.reason
                    : createGameBlockedByOrder
                      ? `Fill Next on court ${orderBlockCourtLabel} completely (${playersPerSide} per team) before creating this match`
                      : isCompletelyEmpty
                        ? "Create a new game from the queue"
                        : "Create Game button only works when all slots are empty"
                }
              >
                Create Game
              </Button>
              
              {!canMatchmake && isCompletelyEmpty && (
                <p className="text-sm text-text-secondary italic max-w-md">
                  ⚠️ {matchmakingValidation.reason}
                </p>
              )}

              {createGameBlockedByOrder && isCompletelyEmpty && canMatchmake && (
                <p className="text-sm text-text-secondary italic max-w-md">
                  Complete Next on court {orderBlockCourtLabel} with a full game before using Create Game here
                  (keeps matchmaking order).
                </p>
              )}
              
              {!isCompletelyEmpty && (
                <p className="text-sm text-text-secondary italic">
                  {rosterLocked
                    ? 'Use × on this match or in Scheduled games to cancel the scheduled game before using Create Game'
                    : 'Remove all players first to use Create Game'}
                </p>
              )}
            </div>
            
          </div>
        ) : null
      }
      isUpcoming={true} // Mark this as an upcoming match for visual distinction
      density={gridDensity}
      minHeight={isPegBoardEmptyCourt ? (gridDensity === 'compact' ? 208 : 300) : 'auto'}
      highlightCallToAction={isPegBoardEmptyCourt && showPegBoardPick}
      teamsLayout={courtCardLayout === 'mobilePlayer' ? 'sideBySide' : 'stack'}
      viewerPlayerId={viewerPlayerId}
      hideTeams={isPegBoardEmptyCourt}
      emptyBody={
        isPegBoardEmptyCourt ? (
          <PegBoardWaitingForGame
            compact={gridDensity === 'compact'}
            action={showPegBoardPick ? pegBoardPickButton : null}
          />
        ) : null
      }
      banner={
        isPegBoardEmptyCourt && pegBoardPickIsViewerTurn ? (
          <PegBoardYourTurnHint compact={gridDensity === 'compact'} />
        ) : pegBoardPicker ? (
              <PegBoardPickerHint player={pegBoardPicker} compact={gridDensity === 'compact'} />
            ) : pegBoardFillFirstLabel ? (
              <PegBoardFillFirstHint
                targetLabel={pegBoardFillFirstLabel}
                compact={gridDensity === 'compact'}
              />
            ) : null
      }
    />
  );
};

export default UpcomingMatch; 